/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native'
import {
  FileGeneric,
  IconDottedLoader,
  IconGreenCheck,
  IconUploadFile,
} from '../../../assets/icons'
import { BtButton } from '@linqpal/components/src/ui'
import { commonColors } from '@linqpal/common-frontend/src/theme'
import { routes } from '../../../../../models'
import ProjectStore from '../Store'
import axios from 'axios'
import numbro from 'numbro'
import { useTranslation } from 'react-i18next'

interface UploadProps {
  docName: S3DocNameType
  onSuccess: (arg: string) => void
  onDelete: () => void
  uploaded?: boolean
}

export const S3_DOC_NAME = {
  NOTICE: 'Notice_of_commencement',
  CONTRACT: 'Contract',
  BOND: 'Bond',
}
type S3DocNameType = typeof S3_DOC_NAME[keyof typeof S3_DOC_NAME]

const STATUS = {
  SUCCESS: 'success',
  PROCESSING: 'processing',
  UPLOADERROR: 'uploadError',
  SIZEERROR: 'sizeError',
}

export const Upload: React.FC<UploadProps> = ({
  docName,
  onSuccess,
  onDelete,
  uploaded = false,
}) => {
  const { t } = useTranslation('global')
  const { setS3Folder, s3Folder } = ProjectStore
  const [document, setDocument] = useState<File>()
  const [status, setStatus] = useState(uploaded ? STATUS.SUCCESS : '')
  const { getRootProps, getInputProps, open } = useDropzone({
    accept: 'image/*, .pdf, .png',
    onDrop: (acceptedFiles) => {
      const formData = new FormData()
      if (acceptedFiles[0].size > 10 * 1024 * 1024) {
        setStatus(STATUS.SIZEERROR)
        return
      }
      for (const file of acceptedFiles) {
        formData.append('file', file)
      }
      console.log('DROPPED', acceptedFiles, s3Folder)
      const name = `${docName}.${acceptedFiles[0].name.split('.').pop()}`
      setStatus(STATUS.PROCESSING)
      setDocument(acceptedFiles[0])
      routes.file
        .uploadProjectDocument(name, s3Folder)
        .then((res) => {
          const { url, key } = res
          const upload = axios.create({
            url,
            method: 'PUT',
            data: acceptedFiles[0],
          })
          upload
            .put(url, acceptedFiles[0])
            .then((result) => {
              console.log('Upload success', result)
              setS3Folder(key.split('/')[2])
              if (result.status === 200) {
                setStatus(STATUS.SUCCESS)
              }
              onSuccess(key)
            })
            .catch(() => setStatus(STATUS.UPLOADERROR))
        })
        .catch(() => setStatus(STATUS.UPLOADERROR))
    },
    multiple: false,
  })

  const name = useMemo(() => {
    if (uploaded && !document?.name) return docName
    else
      return `${document?.name} ${
        document?.size &&
        `- (${numbro(document?.size / 1000 / 1000).format({ mantissa: 2 })}m)`
      }`
  }, [document, docName, uploaded])

  const onPressDelete = () => {
    onDelete()
    setStatus('')
  }

  return (
    <>
      <View
        style={{
          height: 75,
          width: 600,
          borderStyle: 'dashed',
          borderWidth: 1,
          borderRadius: 10,
          borderColor: [STATUS.UPLOADERROR, STATUS.SIZEERROR].includes(status)
            ? '#EC002A'
            : 'rgba(153, 173, 186, 1)',
          paddingHorizontal: 30,
          paddingVertical: 15,
        }}
      >
        {status === STATUS.PROCESSING ? (
          <Processing name={name} />
        ) : status === STATUS.SUCCESS ? (
          <Success name={name} t={t} onDelete={onPressDelete} />
        ) : (
          <View
            {...getRootProps({ className: 'dropzone' })}
            style={{ flexDirection: 'row', alignItems: 'center' }}
          >
            <input {...getInputProps()} />
            <IconUploadFile />
            <View style={{ marginLeft: 15, flex: 1 }}>
              <Text style={styles.text1}>{t('UploadFile.select-file')}</Text>
              <Text style={styles.text2}>{t('UploadFile.accepted-files')}</Text>
            </View>
            <BtButton
              onPress={open}
              style={styles.uploadButton}
              testID="UploadButton"
            >
              {() => (
                <Text style={styles.uploadButtonLabel}>
                  {t('UploadFile.upload-file')}
                </Text>
              )}
            </BtButton>
          </View>
        )}
      </View>
      {[STATUS.UPLOADERROR, STATUS.SIZEERROR].includes(status) && (
        <Text style={styles.error}>
          {STATUS.UPLOADERROR === status
            ? t('UploadFile.on-upload-error')
            : t('UploadFile.on-size-error')}
        </Text>
      )}
    </>
  )
}

const Success: React.FC<{ name: string; t: any; onDelete: () => void }> = ({
  name,
  t,
  onDelete,
}) => {
  return (
    <View style={styles.successContainer}>
      <FileGeneric style={{ marginRight: 7 }} />
      <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center' }}>
        <Text style={styles.fileName}>{name}</Text>
        <IconGreenCheck />
      </View>
      <TouchableOpacity onPress={onDelete}>
        <Text style={styles.delete}>{t('UploadFile.delete')}</Text>
      </TouchableOpacity>
    </View>
  )
}

const Processing: React.FC<{ name }> = ({ name }) => {
  return (
    <>
      <View style={{ flexDirection: 'row', alignItems: 'center' }}>
        <Text style={styles.fileName}>{name}</Text>
        <IconDottedLoader />
      </View>
      <View style={styles.progressBar}>
        <View style={styles.progressBarCompleted} />
      </View>
    </>
  )
}

const styles = StyleSheet.create({
  fileName: {
    fontWeight: '600',
    fontSize: 14,
    lineHeight: 24,
    color: '#3E4756',
    marginRight: 7,
  },
  delete: {
    fontWeight: '700',
    fontSize: 14,
    color: '#00A0F3',
  },
  error: {
    fontWeight: '600',
    fontSize: 13,
    color: '#EC002A',
    marginTop: 7,
  },
  progressBar: {
    width: '100%',
    height: 4,
    borderRadius: 4,
    backgroundColor: '#F4F9FD',
    marginTop: 5,
  },
  progressBarCompleted: {
    width: '40%',
    height: 4,
    borderRadius: 4,
    backgroundColor: commonColors.accentText,
  },
  text1: {
    fontWeight: '600',
    fontSize: 13,
    lineHeight: 15.73,
    color: '#19262F',
    marginBottom: 10,
  },
  text2: {
    fontWeight: '400',
    fontSize: 12,
    lineHeight: 14.52,
    color: '#758590',
  },
  uploadButtonLabel: {
    color: commonColors.accentText,
    fontWeight: '700',
    fontSize: 14,
  },
  uploadButton: {
    width: 116,
    minHeight: 30,
    borderRadius: 8,
    backgroundColor: 'rgba(0, 160, 243, 0.1)',
    border: 'none',
  },
  successContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    height: '100%',
  },
})
